import useSWR from "swr";
import { fetcher } from "@/lib/api";
import { PageHeader, Section } from "@/components/Heading";
import { formatPct } from "@/lib/format";
import { Info } from "lucide-react";
import { useState } from "react";

function tone(p) {
  if (p >= 15) return "bg-destructive/25 text-destructive";
  if (p >= 8) return "bg-warning/20 text-warning";
  return "bg-positive/15 text-positive";
}

export default function HrWeeklyOff() {
  const { data } = useSWR("/hr/weekly-off", fetcher);
  const [explain, setExplain] = useState(false);
  if (!data) return null;
  const days = data.days || ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  // Narrative — count recommended off distribution
  const offCount = {};
  (data.departments || []).forEach((d) => { offCount[d.recommended_off] = (offCount[d.recommended_off] || 0) + 1; });
  const topOff = Object.entries(offCount).sort((a, b) => b[1] - a[1])[0];
  const monAbsTotal = (data.departments || []).reduce((s, d) => s + (d.Mon || 0), 0);
  const monAbsAvg = data.departments?.length ? monAbsTotal / data.departments.length : 0;
  const highRiskDepts = (data.departments || []).filter((d) => (d.reason_pct || 0) >= 15).length;

  return (
    <div>
      <PageHeader
        eyebrow="People · Coverage"
        title="Weekly off planner."
        lede="Shift weekly offs toward the day where each department leaks the most. Weekend coverage stays strong."
      />
      <button
        onClick={() => setExplain(!explain)}
        className="w-full text-left hairline px-4 py-3 mb-3 flex items-center justify-between hover:bg-secondary transition-colors"
        data-testid="weekly-off-logic-toggle"
      >
        <span className="flex items-center gap-2 text-xs"><Info className="w-3.5 h-3.5" /> How this is computed</span>
        <span className="font-mono text-xs text-muted-foreground">{explain ? "Hide" : "Show"}</span>
      </button>

      {/* Narrative strip */}
      <div className="grid md:grid-cols-3 gap-px bg-border mb-10">
        <div className="bg-background p-5">
          <div className="label-caps text-primary mb-2">Most-suggested off</div>
          <div className="font-display text-xl">{topOff?.[0] || "—"} · {topOff?.[1] || 0} depts</div>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            Where coverage gaps cluster. Roll this through the next HR-policy cycle.
          </p>
        </div>
        <div className="bg-background p-5">
          <div className="label-caps text-warning mb-2">Monday tax</div>
          <div className="font-display text-xl">{formatPct(monAbsAvg)} avg absent</div>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            Indian retail's classic Monday slump. If this is high, footfall planning needs to shift Mon → Tue.
          </p>
        </div>
        <div className="bg-background p-5">
          <div className="label-caps text-destructive mb-2">High-risk depts</div>
          <div className="font-display text-xl">{highRiskDepts} above 15%</div>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            Departments with a single day of &gt;15% absent. These are your weekly-off rotation candidates first.
          </p>
        </div>
      </div>
      {explain && (
        <div className="hairline p-4 mb-6 bg-secondary/40 text-sm" data-testid="weekly-off-info">
          For each department we measure day-of-week absenteeism over the last 60 days. The recommended weekly off is the day with the *highest* absent %, on the assumption that absentees were going to be off anyway. This frees up Saturday and Sunday for full coverage.
        </div>
      )}
      <div className="hidden md:block hairline overflow-x-auto">
        <table className="w-full table-dense">
          <thead>
            <tr>
              <th>Department</th>
              {days.map((d) => <th key={d} className="text-center">{d}</th>)}
              <th className="text-center">Recommended off</th>
            </tr>
          </thead>
          <tbody>
            {data.departments.map((d) => (
              <tr key={d.department} data-testid="weekly-off-row">
                <td className="font-display text-sm">{d.department}</td>
                {days.map((day) => (
                  <td key={day} className={`text-center ${tone(d[day])}`}>
                    {d[day]}%
                  </td>
                ))}
                <td className="text-center text-primary font-mono">{d.recommended_off}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:hidden space-y-2">
        {data.departments.map((d) => (
          <div key={d.department} className="hairline p-3">
            <div className="flex justify-between items-baseline">
              <div className="font-display text-sm">{d.department}</div>
              <div className="text-xs text-primary font-mono">→ {d.recommended_off}</div>
            </div>
            <div className="grid grid-cols-7 gap-1 mt-2">
              {days.map((day) => (
                <div key={day} className={`text-center text-[10px] py-1 ${tone(d[day])}`}>
                  <div>{day.slice(0,1)}</div>
                  <div className="font-mono">{d[day]}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
